import {
  CheckCircleOutline as CheckCircleOutlineIcon,
  Edit as EditIcon,
  Error as ErrorIcon,
  ImportExport as ImportExportIcon,
} from '@mui/icons-material';
import { Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { isEmpty } from 'lodash';
import { useEffect, useMemo, useRef } from 'react';
import { useControls } from 'react-zoom-pan-pinch';
import { SECTION_NAME_FONT } from 'src/constants';
import {
  getZIndexStylesFromSectionStyle,
  handleEventStyle,
} from 'src/features/assemblies/editAssemblyPage/EditAssemblyPage.helper';
import { ComponentTypes, DynamicSectionEvents } from 'src/interfaces';
import { getSectionPreviewData } from 'src/utils/gcf-helper';

import { GcfComponentSection } from '../gcfComponentSection';
import { FullSizeAssemblySectionProps } from './FullSizeAssemblySection.interface';
import {
  AutoAwesomeMosaicIconStyled,
  AutorenewIconStyled,
  FullSizeAssemblySectionContainer,
  FullSizeAssemblySectionIconContainer,
  OriginalAssemblyIconContainer,
  RotateIconContainer,
  SubdirectoryArrowRightIconStyled,
} from './FullSizeAssemblySection.styled';

export const FullSizeAssemblySection = ({
  showEditIcon,
  showReplaceIcon,
  showWarningIcon,
  selectable,
  isSelected,
  assemblyComponents,
  showParentIcon,
  showChildIcon,
  onClick,
  activeAssemblyJourney,
  section,
  sectionTypes,
  isCodConfirmationOpen,
  isDisabled,
  hasDaypartSelected = false,
  isDisallowedOutdoorSection,
  sectionName,
  type,
  customerState,
  showSectionBorder = true,
  productFilters,
  productVariationFilters,
  skipProductsApiOnEmptyFilters,
  selectedDaypartId,
}: FullSizeAssemblySectionProps) => {
  const iconRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();

  const { instance } = useControls();

  const screenStyle = handleEventStyle(
    activeAssemblyJourney,
    section.config?.events,
    1,
    sectionTypes,
  );

  const iconZIndex = getZIndexStylesFromSectionStyle(screenStyle);
  const isCodComponent = section.config?.suggestedComponentTypes?.includes(
    ComponentTypes.COD,
  );
  const isCodConfirmationActive = isCodComponent && isCodConfirmationOpen;

  // TODO: Updating size of section which will display COD to center the icon when cod confirmation is displayed. Need to refactor the logic to handel size updated for COD.
  const sectionHeight = isCodConfirmationActive ? 1920 : section.height;
  const sectionWidth = isCodConfirmationActive ? 1080 : section.width;

  // Preview without daypart: only GLOBAL component sections use #CDD3DB; others keep defaults
  const bgColor = !hasDaypartSelected
    ? isDisallowedOutdoorSection && !isEmpty(assemblyComponents)
      ? '#CDD3DB'
      : isDisabled
      ? 'brand.disabled'
      : 'primary.lighter'
    : !isEmpty(assemblyComponents)
    ? 'secondary.main'
    : section.config?.dynamicHighlight! > 0
    ? '#CDD3DB'
    : isDisallowedOutdoorSection && isEmpty(assemblyComponents)
    ? theme.palette.divider
    : isDisabled
    ? 'brand.disabled'
    : 'primary.lighter';

  const selectedStyle = isSelected
    ? {
        borderWidth: '3px',
        borderStyle: 'solid',
        borderColor: 'brand.green',
      }
    : undefined;

  const gcfAssemblyComponents =
    assemblyComponents?.map((item) => {
      return {
        ...(item.id != null || item.uid != null
          ? { id: item.id ?? item.uid }
          : {}),
        ...(item.component_id != null
          ? { componentId: item.component_id }
          : {}),
        rotationOrder: item?.rotation_order,
        config: item?.config,
        backupOrder: item?.backup_order,
        component: {
          ...(item.component_id != null ? { id: item.component_id } : {}),
          type: item?.component?.component_type?.name,
          config: item.component?.config,
          name: item.component?.name,
          width: item.component?.width,
          height: item.component?.height,
          theme: item.component?.theme?.name,
        },
      };
    }) ?? [];

  const gcfAssemblyComponentsKey = useMemo(
    () =>
      assemblyComponents
        ?.map((item) =>
          [
            item.uid ?? item.id,
            item.component_id,
            item.rotation_order,
            item.backup_order,
            item.config?.duration,
            item.config?.transition,
          ].join(':'),
        )
        .join('|') ?? '',
    [assemblyComponents],
  );

  // Keep icon size proportional
  useEffect(() => {
    return instance.onChange((ctx) => {
      if (iconRef.current) {
        const positionX = 0;
        const positionY = 0;
        iconRef.current.style.transform = instance.handleTransformStyles(
          positionX,
          positionY,
          1 / ctx.instance.transformState.scale,
        );
      }
    });
  }, [instance]);

  return (
    <FullSizeAssemblySectionContainer
      width={sectionWidth}
      height={sectionHeight}
      $leftOffset={section.left_offset}
      $topOffset={section.top_offset}
      $selectable={isSelected ? false : selectable}
      $isSelected={isSelected}
      $screenStyle={screenStyle}
      $initialStyle={section.config?.initial?.style}
      type={type}
      onClick={selectable ? onClick : undefined}
      bgcolor={bgColor}
      $isDisallowedOutdoorSection={isDisallowedOutdoorSection}
      $showSectionBorder={showSectionBorder}
      data-testid="assembly-screen-section">
      {!isEmpty(assemblyComponents) && (
        <>
          {assemblyComponents &&
            assemblyComponents.filter((item) => item.backup_order === 0)
              .length > 1 && (
              <RotateIconContainer $zIndex={iconZIndex}>
                <AutorenewIconStyled color="primary" />
              </RotateIconContainer>
            )}
          {showParentIcon && (
            <OriginalAssemblyIconContainer $zIndex={iconZIndex}>
              <AutoAwesomeMosaicIconStyled />
            </OriginalAssemblyIconContainer>
          )}
          {showChildIcon && (
            <OriginalAssemblyIconContainer $zIndex={iconZIndex}>
              <SubdirectoryArrowRightIconStyled />
            </OriginalAssemblyIconContainer>
          )}
          {isCodComponent ? (
            <GcfComponentSection
              key={`${section.screenSectionId}-${gcfAssemblyComponentsKey}-${customerState}-${selectedDaypartId}`}
              screenSectionId={section.screenSectionId}
              assemblyComponents={gcfAssemblyComponents}
              height={sectionHeight}
              width={sectionWidth}
              previewData={getSectionPreviewData(
                assemblyComponents?.[0].component?.component_type
                  ?.name as ComponentTypes,
                isCodConfirmationOpen
                  ? DynamicSectionEvents.COD_CONFIRMATION_OPEN
                  : DynamicSectionEvents.COD_ORDER_OPEN,
                customerState,
              )}
              containerStyle={selectedStyle}
              dimPreview={!hasDaypartSelected && !!isDisallowedOutdoorSection}
              productFilters={productFilters}
              productVariationFilters={productVariationFilters}
              skipProductsApiOnEmptyFilters={skipProductsApiOnEmptyFilters}
              scale={1}
            />
          ) : (
            <GcfComponentSection
              key={`${section.screenSectionId}-${gcfAssemblyComponentsKey}-${customerState}-${selectedDaypartId}`}
              screenSectionId={section.screenSectionId}
              assemblyComponents={gcfAssemblyComponents}
              height={sectionHeight}
              width={sectionWidth}
              previewData={{ sectionConfig: section.config }}
              containerStyle={selectedStyle}
              dimPreview={!hasDaypartSelected && !!isDisallowedOutdoorSection}
              productFilters={productFilters}
              productVariationFilters={productVariationFilters}
              skipProductsApiOnEmptyFilters={skipProductsApiOnEmptyFilters}
              scale={1}
            />
          )}
        </>
      )}

      {showEditIcon && (
        <>
          {showWarningIcon ? (
            <FullSizeAssemblySectionIconContainer
              ref={iconRef}
              bgcolor="primary.lighter"
              $isVertical={section.height > section.width}>
              <ErrorIcon color="error" />
            </FullSizeAssemblySectionIconContainer>
          ) : (
            <FullSizeAssemblySectionIconContainer
              ref={iconRef}
              bgcolor="primary.lighter"
              $isVertical={section.height > section.width}>
              <EditIcon color="primary" style={{ padding: '2px' }} />
            </FullSizeAssemblySectionIconContainer>
          )}
        </>
      )}

      {sectionName && (
        <Typography
          variant="body1"
          sx={{ fontSize: `${SECTION_NAME_FONT}rem` }}>
          {sectionName}
        </Typography>
      )}

      {showReplaceIcon && !isSelected && (
        <FullSizeAssemblySectionIconContainer
          bgcolor="primary.lighter"
          $isVertical={section.height > section.width}>
          <ImportExportIcon color="primary" />
        </FullSizeAssemblySectionIconContainer>
      )}

      {showReplaceIcon && isSelected && (
        <FullSizeAssemblySectionIconContainer
          bgcolor="primary.lighter"
          $isVertical={section.height > section.width}>
          <CheckCircleOutlineIcon color="success" />
        </FullSizeAssemblySectionIconContainer>
      )}
    </FullSizeAssemblySectionContainer>
  );
};
